import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { useApiGet } from "@/lib/apiCache";
import { GAMES, FAMILY } from "@/lib/games";
import { formatDate } from "@/lib/format";
import { CaretLeft } from "@phosphor-icons/react";

export default function PreviousResults({ slug }) {
  const game = GAMES.find((g) => g.slug === slug);
  const { data, loading } = useApiGet(`/results/${slug}?limit=500`);
  const rows = data || [];
  const [year, setYear] = useState("all");
  const [month, setMonth] = useState("all");
  const [page, setPage] = useState(1);
  const pageSize = 30;

  const years = useMemo(
    () => Array.from(new Set(rows.map((r) => r.result_date.slice(0, 4)))).sort((a, b) => b.localeCompare(a)),
    [rows]
  );

  const filtered = useMemo(() => rows.filter((r) => {
    if (year !== "all" && r.result_date.slice(0, 4) !== year) return false;
    if (month !== "all" && r.result_date.slice(5, 7) !== month) return false;
    return true;
  }), [rows, year, month]);

  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));

  if (!game) return null;
  const f = FAMILY[game.color_family];
  const path = `/${slug}`;

  return (
    <div className="min-h-screen flex flex-col bg-neutral-50">
      <SEO
        title={`${game.name} Previous Result | Historical FR & SR Results | Juwai Teer`}
        description={`Complete historical FR and SR Teer results for ${game.name}. Browse past results, filter by year and month.`}
        path={`${path}/previous-result`}
      />
      <Header />

      <section className={`${f.head}`}>
        <div className="max-w-5xl mx-auto px-4 py-6">
          <Link to={path} className="inline-flex items-center gap-1 text-xs opacity-90 hover:opacity-100" data-testid="back-to-game">
            <CaretLeft size={14} weight="bold" /> Back to {game.name}
          </Link>
          <h1 className="font-heading font-black tracking-tight text-2xl sm:text-3xl mt-2" data-testid="prev-h1">
            {game.name} — Previous Results
          </h1>
        </div>
      </section>

      <main className="flex-1 max-w-5xl w-full mx-auto px-4 py-6">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <select
            className="px-3 py-2 rounded-md border border-neutral-300 bg-white text-sm"
            value={year}
            onChange={(e) => { setYear(e.target.value); setPage(1); }}
            data-testid="filter-year"
          >
            <option value="all">All Years</option>
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <select
            className="px-3 py-2 rounded-md border border-neutral-300 bg-white text-sm"
            value={month}
            onChange={(e) => { setMonth(e.target.value); setPage(1); }}
            data-testid="filter-month"
          >
            <option value="all">All Months</option>
            {["01","02","03","04","05","06","07","08","09","10","11","12"].map((m, i) => (
              <option key={m} value={m}>{["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][i]}</option>
            ))}
          </select>
          <div className="text-xs text-neutral-500 ml-auto">{filtered.length} rows</div>
        </div>

        <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500">
                <th className="px-4 py-2.5 font-bold">Date</th>
                <th className="px-4 py-2.5 font-bold">F/R</th>
                <th className="px-4 py-2.5 font-bold">S/R</th>
              </tr>
            </thead>
            <tbody data-testid="prev-table">
              {loading ? (
                <tr><td colSpan={3} className="p-6 text-center text-neutral-500">Loading…</td></tr>
              ) : paginated.length === 0 ? (
                <tr><td colSpan={3} className="p-6 text-center text-neutral-500">No results found.</td></tr>
              ) : (
                paginated.map((r) => (
                  <tr key={r.id} className="border-t border-neutral-100 hover:bg-neutral-50">
                    <td className="px-4 py-2.5">{formatDate(r.result_date)}</td>
                    <td className={`px-4 py-2.5 font-numbers font-bold ${f.num}`}>{r.fr ?? "--"}</td>
                    <td className={`px-4 py-2.5 font-numbers font-bold ${f.num}`}>{r.sr ?? "--"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {filtered.length > pageSize && (
          <div className="mt-4 flex items-center justify-between">
            <div className="text-xs text-neutral-500">Page {page} of {totalPages}</div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 rounded-md border border-neutral-300 text-sm disabled:opacity-50"
                data-testid="prev-page"
              >Previous</button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 rounded-md border border-neutral-300 text-sm disabled:opacity-50"
                data-testid="next-page"
              >Next</button>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
